import { getShelterNow } from '@Constants';
import { getDefaultDateFormat } from '@Helpers';

// NOTE: Format a Date as `YYYY-MM-DD` from LOCAL components. We deliberately avoid `toISOString()`
// because these are date-only values (rental start/due/return) — UTC would shift the day for users
// west of UTC in the evening.
function localDateToIsoString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function getMonthStartIsoDate(): string {
  const date = getShelterNow();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');

  return `${year}-${month}-01`;
}

// NOTE: "Today" means the SHELTER's today, not the browser's. This feeds saved values
// (`rental_start_date`, `created_at`), so a staff member working from another timezone used to
// stamp a rental with their own calendar day — off by one whenever the two clocks straddle midnight.
function getTodayIsoDate(): string {
  return localDateToIsoString(getShelterNow());
}

/**
 * ISO `YYYY-MM-DD` → the SHELTER's display format. Was hardcoded `MM/DD/YYYY`, which showed US
 * order on every shelter.
 *
 * Only the literal date part is formatted, so the day can never shift: the value stays a plain
 * `YYYY-MM-DD` string that `getDefaultDateFormat` parses as local midnight. Passing a full
 * timestamp through instead would convert it into the VIEWER's timezone and could move the day.
 */
function formatIsoDateForDisplay(value: string | null | undefined): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';

  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!isoMatch) return trimmed;

  const [isoDate] = isoMatch;

  return getDefaultDateFormat(isoDate) || trimmed;
}

/** ISO `YYYY-MM-DD` → local {@link Date} for {@link MFieldDatePicker} (avoids UTC shift). */
function isoToLocalDate(iso: string): Date | undefined {
  const trimmed = iso?.trim();
  if (!trimmed) return undefined;

  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return undefined;

  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** {@link MFieldDatePicker} may bind a {@link Date}; API payloads need `YYYY-MM-DD`. */
function toIsoDateString(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.trim();
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : localDateToIsoString(value);
  }

  const parsed = new Date(value as number | string);

  return Number.isNaN(parsed.getTime()) ? '' : localDateToIsoString(parsed);
}

export { formatIsoDateForDisplay, getMonthStartIsoDate, getTodayIsoDate, isoToLocalDate, toIsoDateString };
